import React, {
	type PropsWithChildren,
	createContext,
	useCallback,
	useMemo,
	useEffect,
} from 'react';
import { useCrossFlow, Targets, Journeys } from '@atlassiansox/cross-flow-component-support';
import { useFireCrossFlowAnalyticsEvents } from '../../common/analytics/cross-flow-analytics';
import { useProjectPages } from '../project-pages';
import { useTemplate } from '../template';
import { isConfluenceTemplate } from '../template/utils';
import { type GetConfluenceViewProps } from './types';
import {
	PROJECT_PAGES_SOURCE_COMPONENT,
	PROJECT_PAGES_SOURCE_CONTEXT,
} from '../../common/constants';
import { type MessageDescriptor } from 'react-intl-next';
import messages from '../../ui/project-pages-improvement/messages';
import { type ActivationMethod } from '../../state/activation/types';
import { type GenericButtonOptions } from '../../common/types';
import {
	ExperiencePerformanceTypes,
	ExperienceTypes,
	UFOExperience,
	useUFOComponentExperience,
} from '@atlassian/ufo';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fireOperationalAnalytics, fireUIAnalytics } from '@atlassian/analytics-bridge';
import { getTargetingGate, getExperimentValue } from '../../common/feature-gates';

const GetConfluenceViewContext = createContext<{
	onViewed: () => void;
	shouldDisplayTermsAndConditions: boolean;
	primaryAction: GenericButtonOptions;
	secondaryAction: GenericButtonOptions;
}>({
	onViewed: () => {},
	shouldDisplayTermsAndConditions: false,
	primaryAction: {
		onClick: () => {},
		isLoading: false,
		text: messages.tryButton,
	},
	secondaryAction: {
		onClick: () => {},
		isLoading: false,
		text: messages.learnMoreButton,
	},
});

const primaryActionOptionsPerMethod: Record<ActivationMethod, { text: MessageDescriptor }> = {
	integrated: {
		text: messages.tryOnDemandButton,
	},
	modal: {
		text: messages.tryButton,
	},
	loading: {
		text: messages.tryOnDemandButton,
	},
};

const primaryActionOptionsPerMethodVariation: Record<
	ActivationMethod,
	{ text: MessageDescriptor }
> = {
	integrated: {
		text: messages.tryItNow,
	},
	modal: {
		text: messages.tryItNow,
	},
	loading: {
		text: messages.tryOnDemandButton,
	},
};

function getPrimaryActionOptions(): Record<ActivationMethod, { text: MessageDescriptor }> {
	const cohort = getExperimentValue('project-pages-cta-content-update', 'not-enrolled', true);

	switch (cohort) {
		case 'variation':
			return primaryActionOptionsPerMethodVariation;
		case 'control':
		case 'not-enrolled':
		default:
			return primaryActionOptionsPerMethod;
	}
}

const ufoPrimaryActionLoadTracker = new UFOExperience('get-confluence-primary-action', {
	performanceType: ExperiencePerformanceTypes.PageSegmentLoad,
	type: ExperienceTypes.Load,
	platform: {
		component: 'project-pages',
	},
});

const ufoViewComponentLoadTracker = new UFOExperience('get-confluence-page', {
	performanceType: ExperiencePerformanceTypes.PageSegmentLoad,
	type: ExperienceTypes.Load,
	platform: {
		component: 'project-pages',
	},
});

const GetConfluenceViewProvider = ({
	onOpenCrossFlowError,
	onSuccessfulCrossFlowExpansion,
	activationMethod,
	triggerOnDemandActivation,
	children,
}: PropsWithChildren<GetConfluenceViewProps>) => {
	useUFOComponentExperience(ufoViewComponentLoadTracker);
	const crossFlow = useCrossFlow();
	const { projectKey: jiraProjectKey } = useProjectPages();
	const { template } = useTemplate();
	const { fireViewedEvent, fireClickedEvent } = useFireCrossFlowAnalyticsEvents();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		fireOperationalAnalytics(createAnalyticsEvent({}), 'activationMethod set', {
			activationMethod,
		});
	}, [activationMethod, createAnalyticsEvent]);

	const onViewed = useCallback(() => {
		fireViewedEvent();
		ufoViewComponentLoadTracker.success();
		ufoPrimaryActionLoadTracker.start();
	}, [fireViewedEvent]);

	const openCrossFlowOrDisplayError = useCallback(
		async (journey: typeof Journeys.DECIDE | typeof Journeys.GET_STARTED) => {
			if (crossFlow.isEnabled && jiraProjectKey && isConfluenceTemplate(template)) {
				const crossFlowCompletionStatus = await crossFlow.api.open({
					sourceComponent: PROJECT_PAGES_SOURCE_COMPONENT,
					sourceContext: PROJECT_PAGES_SOURCE_CONTEXT,
					targetProduct: Targets.CONFLUENCE,
					journey,
					experimentalOptions: {
						isCrossSell: true,
						jiraProjectKey,
						isPagesImprovementExperiment: true, // TODO remove after growth-kit is cleaned up (go/j/MLTP-122)
						confluenceBlueprintModuleCompleteKey: template.blueprintModuleCompleteKey,
					},
				});
				if (crossFlowCompletionStatus.success) {
					onSuccessfulCrossFlowExpansion();
				}
			} else {
				onOpenCrossFlowError();
			}
		},
		[crossFlow, jiraProjectKey, template, onSuccessfulCrossFlowExpansion, onOpenCrossFlowError],
	);

	const onSecondaryActionClick = useCallback(() => {
		fireClickedEvent('learnMoreButton', {
			activationMethod,
		});
		openCrossFlowOrDisplayError(Journeys.DECIDE);
	}, [fireClickedEvent, openCrossFlowOrDisplayError, activationMethod]);

	const onPrimaryActionClick = useCallback(() => {
		fireClickedEvent('tryThisTemplateButton', {
			activationMethod,
		});
		if (activationMethod === 'integrated') {
			triggerOnDemandActivation();
		} else {
			openCrossFlowOrDisplayError(Journeys.GET_STARTED);
		}
	}, [fireClickedEvent, activationMethod, triggerOnDemandActivation, openCrossFlowOrDisplayError]);

	const onPrimaryActionReady = useCallback(() => {
		ufoPrimaryActionLoadTracker.success();
		const experimentAttributes = {
			'project-pages-cta-content-update': getTargetingGate('project-pages-cta-content-update-gate')
				? getExperimentValue('project-pages-cta-content-update', 'not-enrolled', true)
				: 'not-enrolled',
			'project-pages-cta-content-update-aa': getExperimentValue(
				'project-pages-cta-content-update-aa',
				'not-enrolled',
				true,
			),
		} as const;

		fireUIAnalytics(createAnalyticsEvent({}), 'tryButton viewed', {
			activationMethod,
			...experimentAttributes,
		});
	}, [activationMethod, createAnalyticsEvent]);

	const primaryAction = useMemo(() => {
		const primaryActionOptions = getTargetingGate('project-pages-cta-content-update-gate')
			? getPrimaryActionOptions()
			: primaryActionOptionsPerMethod;

		return {
			isLoading: activationMethod === 'loading',
			onClick: onPrimaryActionClick,
			onReady: onPrimaryActionReady,
			...primaryActionOptions[activationMethod],
		};
	}, [activationMethod, onPrimaryActionClick, onPrimaryActionReady]);

	const secondaryAction = useMemo(
		() => ({
			isLoading: false,
			onClick: onSecondaryActionClick,
			text: messages.learnMoreButton,
		}),
		[onSecondaryActionClick],
	);

	return (
		<GetConfluenceViewContext.Provider
			value={{
				onViewed,
				primaryAction,
				secondaryAction,
				shouldDisplayTermsAndConditions: activationMethod === 'integrated',
			}}
		>
			{children}
		</GetConfluenceViewContext.Provider>
	);
};

export const useGetConfluenceViewContext = () => React.useContext(GetConfluenceViewContext);
export default GetConfluenceViewProvider;
